import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { PlaceholderRows, Table, tableTypes, tableUtils } from 'core-components/library/list'
import { Loader } from 'core-components/library/loader'
import {
  AbsolutePopup,
  IPopupContentData,
  PopPaper,
  TPopupVertical,
  useTargetSize,
} from 'core-components/library/popup'

import { ColumnRender } from './components/ColumnRender'
import { SelectPlaceholder } from './components/SelectPlaceholder'
import { tableClass } from './constants'
import { ISelectProps } from './models/ISelect'

import styles from './Select.module.scss'

export function Select<T = IForm, R = number | string>({
  source,
  caption,
  children,
  disabled,
  onClick,
  useRowCheckbox,
  onDataLoad,
  onCheck,
  checkedValues,
  popupHeader,
  renderFunction,
  isSelect,
  classes,
  margin,
  wip,
  visibilityCallback,
  hasHierarchy,
  expanded,
  _popupVisible,
  __targetTestId,
  initializePopupRef,
  itemClickCallback,
  popupWidth = 'byTarget',
  hierarchyField = 'HasChildren',
  closeOnSelect = true,
}: ISelectProps<T, R>) {
  const { popupRef, targetWidth } = useTargetSize()
  const [reloadButtonVisible, setReloadButtonVisible] = useState<boolean>(false)
  const [updateCounter, setUpdateCounter] = useState<number>(0)

  if (initializePopupRef) {
    initializePopupRef(popupRef)
  }
  const reloadClickHandler = useCallback(() => {
    setUpdateCounter((prev) => prev + 1)
  }, [])

  const isItemSelectable = useCallback(
    (item: T) => {
      if (item instanceof Object && item.hasOwnProperty('blockClickHandler')) {
        console.error(`Deprecated property, change by new callback "itemClickCallback"`)
      }
      if (itemClickCallback) {
        const result = itemClickCallback(item)
        if (typeof result === 'boolean') {
          return result
        }
      }
      return true
    },
    [itemClickCallback]
  )

  useEffect(() => {
    if (_popupVisible) {
      popupRef.current?.open()
    }
  }, [_popupVisible, popupRef])

  const content = (popupData: IPopupContentData) => {
    return !wip ? (
      <Table<T, R>
        className={classNames(tableClass, classes?.tableRoot)}
        isSelect={isSelect}
        expanded={expanded}
        onDataLoad={(items, total) => {
          if (onDataLoad) {
            return onDataLoad(items, total)
          }
          setReloadButtonVisible(false)
        }}
        onDataLoadError={() => {
          setReloadButtonVisible(true)
        }}
        sourceUpdateCount={updateCounter}
        useRowCheckbox={useRowCheckbox}
        onCheck={onCheck}
        checkedValues={checkedValues}
        mode={tableTypes.TableMode.div}
        emptyPageSize={3}
        hasHierarchy={!!source.hierarchyField && hasHierarchy !== false}
        source={source}
        tabIndex={-1}
        columns={[
          {
            field: caption || '',
            title: '',
          },
        ]}
        hierarchyTpl={(item, rowState, hierarchyClick, hierarchyContent) => {
          return (
            <div className={styles.hierarchyCell} tabIndex={1}>
              {hierarchyContent(item, rowState, hierarchyClick)}
            </div>
          )
        }}
        hierarchyContent={(item, rowState, hierarchyClick) => {
          const hasChildren = tableUtils.getValue(item, hierarchyField)
          return (
            <>
              {hasChildren ? (
                rowState.loading ? (
                  <div className={styles.expandPlaceholder}>
                    <Loader dimension={{ height: 20, width: 20 }} />
                  </div>
                ) : (
                  <Button
                    __testId={`select-hierarchy-button-${item[source.keyField]}`}
                    className={classNames(
                      rowState.expanded ? styles.hierarchyIconExpanded : '',
                      useRowCheckbox ? 'row-button' : ''
                    )}
                    iconButton={
                      rowState.expanded ? 'kbase kbase-caret-down' : 'kbase kbase-caret-right'
                    }
                    variant={'text'}
                    color={'grey'}
                    onClick={(e) => {
                      e.target.closest('[tabindex]')?.focus()
                      hierarchyClick(item, hierarchyField)
                      e.stopPropagation()
                    }}
                  />
                )
              ) : (
                <div className={styles.expandPlaceholder} />
              )}
            </>
          )
        }}
        rowTpl={(item, renderChildren, rowState, rowAttrs, additional) => {
          const { testId, key, className, deep, ...rowAttrsRest } = rowAttrs
          return (
            <div
              {...rowAttrsRest}
              data-testid={testId}
              key={key}
              className={classNames(className, styles.row)}
              style={{
                paddingLeft: deep ? `${deep * 16}px` : undefined,
              }}
              tabIndex={1}
            >
              {useRowCheckbox && (
                <Checkbox
                  __testId={`select-checkbox-${item[source.keyField]}`}
                  className={classNames(styles.checkbox, 'row-checkbox')}
                  indeterminate={rowState.indeterminate}
                  checked={rowState.selected}
                  onChange={(e) => {
                    if (onCheck) {
                      onCheck(item, e)
                    }
                  }}
                />
              )}
              {renderChildren(item, rowState, additional)}
            </div>
          )
        }}
        cellTpl={(item: T, column, cellAttrs) => {
          return (
            <div
              {...cellAttrs}
              key={cellAttrs.key}
              data-testid={`select-cell-${column.field}-${item[source.keyField]}`}
              className={classNames(
                cellAttrs.className,
                styles.cell,
                column.field === 'icon' ? styles.cell_icon : '',
                'cmp-Select-cell'
              )}
            >
              {renderFunction ? (
                renderFunction(item)
              ) : (
                <ColumnRender item={item as IForm} column={column} caption={caption} />
              )}
            </div>
          )
        }}
        onClick={async (item, { hierarchyClick, isExpanded }) => {
          const canChoose = isItemSelectable(item)
          if (!canChoose) {
            return
          }
          const select = (element: T) => {
            onClick(element)
            if (closeOnSelect) {
              popupData.action.close()
            } else {
              popupData.action.focus()
            }
          }

          if (tableUtils.getValue(item, hierarchyField)) {
            const currentExpanded = isExpanded(item, hierarchyField)
            if (currentExpanded) {
              select(item)
            } else {
              const rowInfo = await hierarchyClick(item, hierarchyField)
              if (!rowInfo.hasChildren) {
                select(item)
              }
            }
          } else {
            select(item)
          }
        }}
        useHotKeys={true}
        placeholderContent={() =>
          reloadButtonVisible ? (
            <SelectPlaceholder onReloadClick={reloadClickHandler} />
          ) : (
            'Nothing to show'
          )
        }
      />
    ) : (
      <table className={styles.placeholder} data-testid={'select-table-placeholder'}>
        <tbody>
          <PlaceholderRows columnsCount={1} rowsCount={5} noData={false} hasHierarchy={true} />
        </tbody>
      </table>
    )
  }
  return (
    <AbsolutePopup
      ref={popupRef}
      width={popupWidth === 'byTarget' ? targetWidth : undefined}
      popupClassName={styles.popupWrap}
      rootClassName={classNames(
        {
          [`margin-bottom-${margin}`]: !!margin,
        },
        classes?.root
      )}
      verticalStartPosition={TPopupVertical.Bottom}
      visibilityCallback={(newValue) => {
        if (disabled) {
          return false
        }
        if (visibilityCallback) {
          return visibilityCallback(newValue)
        }
        return newValue
      }}
      popupContent={(popupData) => (
        <PopPaper shadowPosition={'auto'} className={classNames(styles.popup, classes?.popPaper)}>
          {!!popupHeader && popupHeader(popupData)}
          {content(popupData)}
        </PopPaper>
      )}
      __rootTestId={'selectPopupRoot'}
      __targetTestId={__targetTestId}
    >
      {children}
    </AbsolutePopup>
  )
}
